import {Component, computed, inject, signal} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {
  AdminPhaseStatusInfo,
  AdminProjectCard,
  AdminProjectControllerService,
  AdminProviderPhaseStatus,
} from '@frontend/shared-api';
import {LoadingComponent} from '../../../../shared/components/loading/loading.component';
import {ChapterProgressGridComponent} from './chapter-progress-grid/chapter-progress-grid.component';
import {PhaseStatusBarsComponent} from './phase-status-bars/phase-status-bars.component';
import {Panel} from 'primeng/panel';
import {NgClass} from '@angular/common';
import {PanelHeaderComponent} from '../../../../shared/components/header/panel-header/panel-header.component';
import {AppDatePipe} from '@frontend/shared';
import {GlobalSearchComponent} from '../../../../shared/components/search/global-search/global-search.component';
import {DropdownFilterComponent} from '../../../../shared/components/filter/dropdown-filter/dropdown-filter.component';
import {DropdownSubmissionDateFilterComponent} from './dropdown-submission-date-filter/dropdown-submission-date-filter.component';
import {DropdownPhaseFilterComponent} from './dropdown-phase-filter/dropdown-phase-filter.component';
import {hasSubmissionDateWithin, isSubmissionDateStatus} from './project-overview.utils';
import {ProviderStatusLookupService} from './provider-status-table/provider-status-lookup.service';
import {SUBMISSION_DATE_FILTER_ALL} from './dropdown-submission-date-filter/dropdown-submission-date-filter.types';
import {
  FILTER_ALL,
  PHASE_FILTER_ARCHIVED,
} from './dropdown-phase-filter/dropdown-phase-filter.types';

type SortField = 'tenantName' | 'projectName' | 'beginDate' | 'endDate';
type SortDirection = 'ASC' | 'DESC';

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  imports: [
    LoadingComponent,
    ChapterProgressGridComponent,
    PhaseStatusBarsComponent,
    Panel,
    NgClass,
    PanelHeaderComponent,
    AppDatePipe,
    GlobalSearchComponent,
    DropdownFilterComponent,
    DropdownSubmissionDateFilterComponent,
    DropdownPhaseFilterComponent,
  ],
  providers: [ProviderStatusLookupService],
})
export class ProjectOverviewComponent {
  private readonly adminProjectController = inject(AdminProjectControllerService);

  // Filter and sorting
  protected readonly searchText = signal<string>('');
  protected readonly selectedPhase = signal<string>(FILTER_ALL);
  protected readonly selectedSubmissionDate = signal<number>(SUBMISSION_DATE_FILTER_ALL);
  protected readonly sortField = signal<SortField>('projectName');
  protected readonly sortDirection = signal<SortDirection>('ASC');

  protected readonly sortFieldOptions: {label: string; value: SortField}[] = [
    {label: 'common.provider', value: 'tenantName'},
    {label: 'common.project.name', value: 'projectName'},
    {label: 'project.startDate', value: 'beginDate'},
    {label: 'common.endDate', value: 'endDate'},
  ];

  protected readonly projectOverviewResource = rxResource({
    stream: () => this.adminProjectController.loadProjectOverview(),
  });

  protected readonly isLoading = computed(() => {
    return this.projectOverviewResource.isLoading();
  });

  protected readonly filteredProjects = computed<AdminProjectCard[]>(() => {
    const projects = this.projectOverviewResource.value()?.projects ?? [];
    const search = this.searchText().trim().toLowerCase();
    const phase = this.selectedPhase();
    const dayWindow = this.selectedSubmissionDate();

    return projects.filter((project: AdminProjectCard) => {
      const matchesSearch =
        !search ||
        project.tenantName.toLowerCase().includes(search) ||
        project.projectName.toLowerCase().includes(search);
      const matchesPhase = this.matchesPhaseFilter(project, phase);
      const matchesSubmissionDate = this.matchesSubmissionDateFilter(project, dayWindow);
      return matchesSearch && matchesPhase && matchesSubmissionDate;
    });
  });

  protected readonly sortedProjects = computed<AdminProjectCard[]>(() => {
    const projects = [...this.filteredProjects()];
    const field = this.sortField();
    const direction = this.sortDirection() === 'ASC' ? 1 : -1;

    return projects.sort(
      (a: AdminProjectCard, b: AdminProjectCard) => this.compareProjects(a, b, field) * direction,
    );
  });

  private matchesPhaseFilter(project: AdminProjectCard, phase: string): boolean {
    if (phase === FILTER_ALL) return true;
    if (phase === PHASE_FILTER_ARCHIVED) return project.archived;

    return project.phaseStatusInfos.some(
      (phaseStatusInfo: AdminPhaseStatusInfo) =>
        phaseStatusInfo.phase === phase && phaseStatusInfo.phaseLocked,
    );
  }

  private matchesSubmissionDateFilter(project: AdminProjectCard, dayWindow: number): boolean {
    if (dayWindow === SUBMISSION_DATE_FILTER_ALL) return true;

    return project.phaseStatusInfos.some((phaseStatusInfo: AdminPhaseStatusInfo) =>
      phaseStatusInfo.providerStatuses.some(
        (providerStatus: AdminProviderPhaseStatus) =>
          isSubmissionDateStatus(providerStatus.status) &&
          hasSubmissionDateWithin(providerStatus, dayWindow),
      ),
    );
  }

  private compareProjects(a: AdminProjectCard, b: AdminProjectCard, field: SortField): number {
    return String(a[field]).localeCompare(String(b[field]));
  }
}
